import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allComics, categories, categoryInfo } from '@/mocks/comics';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('type') || '全部';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeCategory]);

  const filteredComics = useMemo(() => {
    let result = activeCategory === '全部'
      ? [...allComics]
      : allComics.filter((c) => c.category === activeCategory);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const info = categoryInfo[activeCategory] || categoryInfo['全部'];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${info.name} - 香香漫画`,
    description: info.description,
    url: `https://www.xiangxiangcomic.com/category?type=${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '香香漫画',
      url: 'https://www.xiangxiangcomic.com/',
    },
  };

  return (
    <>
      <SEOMeta
        title={`${info.name} - 香香漫画网：${info.description}`}
        description={`香香漫画${info.name}专区，${info.description}。香香漫画网提供最新最全${activeCategory === '全部' ? '漫画' : activeCategory}资源免费在线阅读。`}
        keywords={`香香漫画,${activeCategory === '全部' ? '韩漫,日漫,国漫' : activeCategory},漫画免费阅读,在线漫画`}
        canonicalUrl={`https://www.xiangxiangcomic.com/category?type=${activeCategory}`}
        schema={schema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: info.name },
            ]}
          />

          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
              {info.name}
            </h1>
            <p className="text-foreground-500 text-sm">{info.description}</p>
          </header>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    if (cat === '全部') {
                      setSearchParams({});
                    } else {
                      setSearchParams({ type: cat });
                    }
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
              {([
                { key: 'latest' as const, label: '最新' },
                { key: 'popular' as const, label: '最热' },
                { key: 'rating' as const, label: '评分' },
              ]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'text-foreground-500 hover:text-foreground-700 bg-background-100 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 mb-12">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-xl bg-background-100 mb-3 aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${comic.category}免费阅读`}
                    title={`${comic.title} - 香香漫画${comic.category}在线`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-0.5 rounded-md bg-background-950/70 text-background-50 text-[10px] font-medium">
                      {comic.category}
                    </span>
                  </div>
                  {comic.status === '已完结' && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-accent-500/80 text-background-50 text-[10px] font-medium">
                      完结
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 pt-8">
                    <div className="flex items-center gap-2 text-background-50 text-xs">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                      </div>
                      <span>{comic.rating}</span>
                      <span className="text-background-300">·</span>
                      <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}阅读</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1 mb-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-400 line-clamp-1">{comic.author}</p>
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂未找到相关漫画</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}